import React, { useRef, useState } from "react";
import { Button, Check, Edit02, TextInput, XClose } from "@opendate-app/setlist";

const styles = {
  identity: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },
  identityDivider: {
    width: 1,
    height: 40,
    flexShrink: 0,
    background: "var(--sl-border-secondary)",
  },
  caption: {
    fontSize: 12,
    color: "var(--sl-text-tertiary)",
    lineHeight: "18px",
  },
  nameRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  name: {
    padding: "0 24px 2px 0",
    textAlign: "left",
    border: "none",
    borderBottom: "1px solid var(--sl-border-secondary)",
    background: "transparent",
    fontSize: 18,
    lineHeight: "28px",
    fontWeight: 600,
    color: "var(--sl-text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "text",
  },
  track: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
  trackItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  connector: (filled) => ({
    width: 40,
    height: 2,
    borderRadius: 1,
    background: filled ? "var(--sl-border-brand)" : "var(--sl-border-secondary)",
  }),
  trackStep: (isCurrent, isDone) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: 0,
    border: "none",
    background: "transparent",
    fontSize: 14,
    fontWeight: isCurrent ? 600 : 500,
    color: isCurrent
      ? "var(--sl-text-primary)"
      : isDone ? "var(--sl-text-secondary)" : "var(--sl-text-quaternary)",
    cursor: "pointer",
  }),
  marker: (isCurrent, isDone) => ({
    width: 24,
    height: 24,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    borderRadius: "50%",
    fontSize: 12,
    fontWeight: 600,
    ...(isCurrent
      ? {
        background: "var(--sl-bg-brand-solid)",
        color: "var(--sl-text-white)",
        boxShadow: "0 0 0 4px var(--sl-bg-brand-secondary)",
      }
      : isDone
        ? { background: "var(--sl-bg-brand-primary)" }
        : {
          border: "1px solid var(--sl-border-secondary)",
          color: "var(--sl-text-quaternary)",
        }),
  }),
};

const PencilIcon = (props) => (
  <Edit02 {...props} width={16} height={16} color="var(--sl-text-quaternary)" />
);

export const BuilderIdentity = ({ noun, caption, name, setName, onSave, onCancel, onClose, inline = false }) => {
  const Box = inline ? "span" : "div";
  const [renaming, setRenaming] = useState(false);
  const discardRename = useRef(false);

  const finishRename = () => {
    setRenaming(false);

    if (discardRename.current) {
      discardRename.current = false;
      onCancel();
      return;
    }

    onSave();
  };

  return (
    <Box style={styles.identity}>
      {onClose && (
        <>
          <Button variant="tertiary" size="sm" icon={XClose} aria-label="Close" onClick={onClose} />
          <span style={styles.identityDivider} aria-hidden="true" />
        </>
      )}
      <Box style={{ minWidth: 0, flex: 1 }}>
        <Box style={inline ? { ...styles.caption, fontWeight: 400, display: "block" } : styles.caption}>{caption}</Box>
        {renaming ? (
          <TextInput
            autoFocus
            aria-label={`${noun[0].toUpperCase()}${noun.slice(1)} name`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={finishRename}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.currentTarget.blur();
              if (event.key === "Escape") {
                discardRename.current = true;
                event.currentTarget.blur();
              }
            }}
          />
        ) : (
          <Box style={styles.nameRow}>
            <button type="button" style={styles.name} onClick={() => setRenaming(true)}>{name}</button>
            <Button variant="tertiary" size="sm" icon={PencilIcon} aria-label={`Rename ${noun}`} onClick={() => setRenaming(true)} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const ProgressTrack = ({ ariaLabel, steps, step, completed, onSelect, inline = false }) => {
  const stepIndex = steps.findIndex((item) => item.value === step);
  const [Nav, List, Item] = inline ? ["span", "span", "span"] : ["nav", "ol", "li"];
  const roles = (role) => (inline ? { role } : {});

  return (
    <Nav aria-label={ariaLabel} {...roles("navigation")} style={inline ? { display: "block" } : undefined}>
      <List style={styles.track} {...roles("list")}>
        {steps.map((item, index) => {
          const isCurrent = index === stepIndex;
          const isDone = completed[item.value];

          return (
            <Item key={item.value} style={styles.trackItem} {...roles("listitem")}>
              {index > 0 && <span style={styles.connector(index <= stepIndex)} aria-hidden="true" />}
              <button
                type="button"
                style={styles.trackStep(isCurrent, isDone)}
                aria-current={isCurrent ? "step" : undefined}
                onClick={() => onSelect(item.value)}
              >
                {isDone && !isCurrent ? (
                  <span style={styles.marker(false, true)}>
                    <Check width={14} height={14} color="var(--sl-fg-brand)" aria-label="Complete" />
                  </span>
                ) : (
                  <span style={styles.marker(isCurrent, false)} aria-hidden="true">{index + 1}</span>
                )}
                {item.label}
                {item.badge}
              </button>
            </Item>
          );
        })}
      </List>
    </Nav>
  );
};
